import React, { forwardRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { View, Text, colors, Heading, Button } from '@fairygodboss/ui';
import { DividerSVG, ConvincerSVG } from './ConvincerSVGs';
import {
    BulletOne,
    BulletTwo,
    BulletThree,
    BulletFour,
    BulletFive,
} from './Bullets';
import { AuthWrapper } from '../../../../../pages/HomePage_v2/sections/components/AuthWrapper';
import { EmitMetric } from '../../../../Analytics/VisibilityPixel/VisibilityPixel';

export const ConvincerButton = ({
    windowWidth,
    text,
    onConvincerClick,
    ...others
}) => {
    const isSM = windowWidth < 905;
    return (
        <Button
            onPress={onConvincerClick}
            mt="8px"
            maxWidth={{ _: '227px', md: '225px' }}
            height="56px"
            px="28px"
            py="14px"
            size="medium"
            fontSize="16px"
            lineHeight="20px"
            alignSelf={{ _: 'auto' }}
            borderRadius={{ _: '56px', md: '4px' }}
            {...(isSM && {
                width: '227px',
            })}
            boxShadow={{ _: '0px 12px 32px rgba(53, 53, 53, 0.4)', md: 'none' }}
            css={{
                minWidth: '120px',
                span: {
                    overflow: 'inherit',
                },
            }}
            {...others}
        >
            {text}
        </Button>
    );
};

ConvincerButton.displayName = 'ConvincerButton';

export const ConvincerWithBullets = () => {
    const bulletPoints = [
        {
            text: 'Daily articles on career topics',
            svg: <BulletOne />,
        },
        {
            text: 'Jobs at companies dedicated to hiring more women',
            svg: <BulletTwo />,
        },
        {
            text: 'Advice and support from an authentic community',
            svg: <BulletThree />,
        },
        {
            text: 'Events that help you level up in your career',
            svg: <BulletFour />,
        },
        {
            text: '<strong>Free</strong> membership, always',
            svg: <BulletFive />,
        },
    ];

    const BulletPoint = ({ text, svg }) => {
        return (
            <View
                as="li"
                lineHeight="20px"
                letterSpacing="-0.4px"
                fontSiize="16px"
                color={colors.neutral[100]}
                css={{
                    display: 'block',
                    backgroundImage: ` url("data:image/svg+xml,${encodeURIComponent(
                        renderToStaticMarkup(svg)
                    )}")`,

                    backgroundRepeat: 'no-repeat',
                    backgroundPositionY: '20px',
                    paddingLeft: '20px',
                    paddingTop: '12px',
                    fontSize: '16px',
                    strong: {
                        fontWeight: 600,
                    },
                }}
                dangerouslySetInnerHTML={{ __html: text }}
            />
        );
    };

    const onEmitMetirc = () => {
        EmitMetric({
            misc_event_type: 'article_join_sticky_button',
            misc_event_count: 1,
        });
    };

    return (
        <View pl={{ _: '0px', md: '12px', lg: '0px' }}>
            <DividerSVG width="100" />
            <Heading fontWeight="700" variant="headingXs" pt="8px" as="h3">
                Why women love us:
            </Heading>
            <View
                as="ul"
                css={{
                    listStyleType: 'none',
                    padding: 0,
                    margin: 0,
                }}
            >
                {bulletPoints.map((j) => (
                    <BulletPoint
                        key={j.text.split(' ').join('-')}
                        text={j.text}
                        svg={j.svg}
                    />
                ))}
            </View>
            <AuthWrapper
                component={
                    <ConvincerButton
                        onConvincerClick={onEmitMetirc}
                        windowWidth={null}
                        text="Join now"
                        width="120px"
                        mt="16px"
                        height="48px"
                    />
                }
                redirectUrl="/community/feed"
                loginTrigger="article_join_sticky_button"
            />
        </View>
    );
};

ConvincerWithBullets.displayName = 'ConvincerWithBullets';

const TopConvincer = ({ windowWidth }) => {
    const onEmitMetirc = () => {
        EmitMetric({
            misc_event_type: 'article_join_banner_button',
            misc_event_count: 1,
        });
    };

    return (
        <>
            <View display="flex" justifyContent="center">
                <ConvincerSVG />
            </View>
            <DividerSVG width="100" />
            <Heading
                fontWeight="700"
                variant="headingXs"
                pt="6px"
                pb="10px"
                as="h3"
            >
                Welcome to Fairygodboss!
            </Heading>
            <Text mb="6px">
                We’re the largest online career community helping women achieve
                their career goals. Gain access to articles, jobs, events, and a
                supportive network of women and allies. Membership is free,
                always.
            </Text>
            <AuthWrapper
                component={
                    <ConvincerButton
                        onConvincerClick={onEmitMetirc}
                        windowWidth={windowWidth}
                        text="Join the community"
                        height="48px"
                        width="201px"
                    />
                }
                redirectUrl="/community/feed"
                loginTrigger="article_join_banner_button"
            />
        </>
    );
};

TopConvincer.displayName = 'TopConvincer';

export const Convincer = forwardRef(({ windowWidth }, ref) => (
    <View
        ref={ref}
        id="top-convincer"
        display={{ _: 'none', md: 'flex' }}
        flexDirection="column"
        width="100%"
        pl={{ _: '0px', md: '12px', lg: '0px' }}
    >
        <TopConvincer windowWidth={windowWidth} />
    </View>
));
